import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { Banco } from 'src/app/dashboard/interfaces/banco';
import { Pais } from 'src/app/dashboard/interfaces/country.interface';
import { TipoCuenta } from 'src/app/dashboard/interfaces/tipo-cuenta.interface';
import { AccountTypeService } from 'src/app/dashboard/services/account-type.service';
import { BanksService } from 'src/app/dashboard/services/banks.service';
import { CountriesService } from 'src/app/dashboard/services/countries.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { CuentaRetiro } from '../../interfaces/cuenta-retiro.interface';
import { WithdrawAccountService } from '../../services/withdraw-account.service';

@Component({
  selector: 'app-add-withdraw-accounts',
  templateUrl: './add-withdraw-accounts.component.html',
  styleUrls: ['./add-withdraw-accounts.component.sass'],
})
export class AddWithdrawAccountsComponent {
  formCuenta: FormGroup | undefined;
  paisesActivos: Pais[] = [];
  tiposID = ['RUT'];
  tiposCuentas: TipoCuenta[] = [];
  bancos: Banco[] = [];
  usuarioID: number | undefined;
  subscriptions: Subscription = new Subscription();
  esUpdate = false;
  preselectedCountry: string = '';
  user: any;
  camposBloqueados = false;

  get isChile(): boolean {
    return this.formCuenta?.get('pais')?.value === 'CL';
  }
  constructor(
    private fb: FormBuilder,
    private countriesService: CountriesService,
    private bancoService: BanksService,
    private tipoCuentaService: AccountTypeService,
    private withdrawAccountService: WithdrawAccountService,
    private spinner: SpinnerService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<AddWithdrawAccountsComponent>,
    private router: Router,
    private store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.esUpdate = this.data.type === 'UPDATE';
    this.preselectedCountry = this.data.preselectedCountry || '';
    this.initFormCuenta();
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async initFormCuenta() {
    const paisInicial = this.esUpdate
      ? this.data.cuenta.codigoPais
      : this.preselectedCountry || '';
    this.formCuenta = this.fb.group({
      pais: new FormControl(paisInicial, [
        Validators.required,
      ]),
      alias: new FormControl(this.esUpdate ? this.data.cuenta.alias : '', [
        Validators.required,
      ]),
      tipoId: new FormControl(this.esUpdate ? this.data.cuenta.tipoId : '', [
        Validators.required,
      ]),
      numeroId: new FormControl(
        this.esUpdate ? this.data.cuenta.numeroId : '',
        [Validators.required]
      ),
      banco: new FormControl(this.esUpdate ? this.data.cuenta.banco : '', [
        Validators.required,
      ]),
      bancoId: new FormControl(this.esUpdate ? this.data.cuenta.bancoId : ''),
      tipoCuentaId: new FormControl(
        this.esUpdate ? this.data.cuenta.tipoCuentaId : '',
        [Validators.required]
      ),
      numeroCuenta: new FormControl(
        this.esUpdate ? this.data.cuenta.numeroCuenta : '',
        [
          Validators.required,
          Validators.maxLength(20),
          Validators.pattern(/^[a-zA-Z0-9]*$/),
        ]
      ),
    });
    this.subscriptions.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.user = user;
        this.prefillIdentityFields();
      })
    );
    this.initLists();
  }

  prefillIdentityFields() {
    if (!this.formCuenta || !this.user || this.esUpdate) return;
    if (!this.isChile || this.user.tipoId === 'VE') {
      if (this.camposBloqueados) {
        this.formCuenta.get('alias')?.enable();
        this.formCuenta.get('tipoId')?.enable();
        this.formCuenta.get('numeroId')?.enable();
        this.formCuenta.get('alias')?.reset('');
        this.formCuenta.get('tipoId')?.reset('');
        this.formCuenta.get('numeroId')?.reset('');
        this.camposBloqueados = false;
      }
      return;
    }
    const isCompany = Boolean(this.user.tipoUsuarioID);
    const nombre = isCompany
      ? (this.user.razonSocial || '')
      : `${this.user.nombre || ''} ${this.user.apellidos || ''}`.trim();
    const numeroId = isCompany
      ? (this.user.rutEmpresa || '')
      : (this.user.numeroId || '');

    this.formCuenta.patchValue({
      alias: nombre,
      tipoId: 'RUT',
      numeroId: numeroId,
    });
    this.formCuenta.get('alias')?.disable();
    this.formCuenta.get('tipoId')?.disable();
    this.formCuenta.get('numeroId')?.disable();
    this.camposBloqueados = true;
  }

  async initLists() {
    this.paisesActivos = await this.countriesService.getActiveCountries();
    this.removePais('VE');
    this.removePais('US');
    if (this.esUpdate) {
      [this.bancos, this.tiposCuentas] = await Promise.all([
        this.bancoService.getBanksByCountry(this.data.cuenta.codigoPais),
        this.tipoCuentaService.getAccountTypes(this.data.cuenta.codigoPais),
      ]);
      const bancoIndex = this.bancos.find(
        (elem: any): boolean => elem.bancoID === this.data.cuenta.bancoId
      );

      this.formCuenta?.get('banco')?.patchValue(bancoIndex);
      this.updateNumeroCuentaValidators();
    } else if (this.preselectedCountry) {
      [this.bancos, this.tiposCuentas] = await Promise.all([
        this.bancoService.getBanksByCountry(this.preselectedCountry),
        this.tipoCuentaService.getAccountTypes(this.preselectedCountry),
      ]);
      this.updateNumeroCuentaValidators();
    }
  }
  async getBanks() {
    const paisSeleccionado = this.formCuenta?.get('pais')?.value;
    if (!paisSeleccionado) return;
    [this.bancos, this.tiposCuentas] = await Promise.all([
      this.bancoService.getBanksByCountry(paisSeleccionado),
      this.tipoCuentaService.getAccountTypes(paisSeleccionado),
    ]);
    this.updateNumeroCuentaValidators();
    this.prefillIdentityFields();
  }

  updateNumeroCuentaValidators() {
    const numeroCuenta = this.formCuenta?.get('numeroCuenta');
    if (!numeroCuenta) return;
    if (this.isChile) {
      numeroCuenta.setValidators([
        Validators.required,
        Validators.maxLength(20),
        Validators.pattern(/^[0-9]*$/),
      ]);
    } else {
      numeroCuenta.setValidators([
        Validators.required,
        Validators.maxLength(20),
        Validators.pattern(/^[a-zA-Z0-9]*$/),
      ]);
    }
    numeroCuenta.updateValueAndValidity();
  }

  removePais(codigoPais: string): void {
    this.paisesActivos = this.paisesActivos.filter(
      (pais) => pais.codigoPais !== codigoPais
    );
  }
  ngOnInit() {}

  async guardarCuentaRetiro() {
    let mensajeExito = 'Cuenta de retiro creada';
    if (this.formCuenta?.invalid) return;
    const formValue = this.formCuenta?.getRawValue();
    const bancoData = this.isChile
      ? { ...formValue.banco, codigoBanco: '' }
      : formValue.banco;
    const cuenta: CuentaRetiro = {
      ...formValue,
      banco: bancoData,
      bancoId: formValue.banco.bancoID,
      tipoCuentaID: formValue.tipoCuenta,
      cuentaId: null,
    };
    if (this.esUpdate) {
      cuenta['cuentaId'] = this.data.cuenta.id;
      mensajeExito = 'Cuenta de retiro actualizada';
    }
    const usuarioID = this.data.userId;
    const ref = this.spinner.start();
    try {
      const resultado = await this.withdrawAccountService.saveWithdrawAccount(
        cuenta,
        usuarioID
      );

      this.snackBar.open(mensajeExito, 'Ok', { duration: 3000 });
      this.dialogRef.close(true);
      if (!usuarioID && !this.preselectedCountry) {
        this.router.navigate(['/wallet/client/withdraw']);
      }
    } catch (error: any) {
      if (error.status === 400) {
        Swal.fire({
          icon: 'warning',
          title: error.error.title,
          text: error.error.message,
          showCancelButton: false,
          showDenyButton: false,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1e5a3f',
        });
      }
    }
    this.spinner.stop(ref);
  }
}
